import type {
  Position,
  PowerUpType,
  PowerUpVariant,
} from './game.interface';

/**
 * Things that happened during one server tick. The client can't tell these
 * apart by diffing consecutive `GameState`s (several score changes merge into
 * one delta, a vanished racing food could be won or expired, …), so the server
 * records them as they happen and broadcasts them with the state.
 *
 * Events are cosmetic input for client effects only — never a second source
 * of truth for scores or positions.
 */
export enum GameEventType {
  FOOD_EATEN = 'FOOD_EATEN',
  RACING_FOOD_SPAWNED = 'RACING_FOOD_SPAWNED',
  RACING_FOOD_WON = 'RACING_FOOD_WON',
  /** Another player won the racing food; it vanishes from this board. */
  RACING_FOOD_TAKEN = 'RACING_FOOD_TAKEN',
  /** Two or more players reached the racing food on the same tick; nobody scores. */
  RACING_FOOD_CONTESTED = 'RACING_FOOD_CONTESTED',
  RACING_FOOD_EXPIRED = 'RACING_FOOD_EXPIRED',
  DEATH = 'DEATH',
  RESPAWN = 'RESPAWN',
  ELIMINATED = 'ELIMINATED',
  OBSTACLE_HIT = 'OBSTACLE_HIT',
  BULLDOZER_SMASH = 'BULLDOZER_SMASH',
  BLOCKS_DESTROYED = 'BLOCKS_DESTROYED',
  BLOCK_DENIED = 'BLOCK_DENIED',
  BLOCK_SOLIDIFIED = 'BLOCK_SOLIDIFIED',
  BLOCK_CHARGED = 'BLOCK_CHARGED',
  PICKUP_COLLECTED = 'PICKUP_COLLECTED',
  /** Walked over with both inventory slots full; the pickup is destroyed. */
  PICKUP_LOST = 'PICKUP_LOST',
  PICKUP_EXPIRED = 'PICKUP_EXPIRED',
  PEAK_LENGTH_BONUS = 'PEAK_LENGTH_BONUS',
  TITLE_GAINED = 'TITLE_GAINED',
  TITLE_LOST = 'TITLE_LOST',
}

export type DeathCause = 'wall' | 'self' | 'obstacle';

/**
 * Fields shared by every event.
 * - `playerId` is the board the event happened on (for title events, the
 *   player who gained or lost it).
 * - `delta` is the score change actually applied, after clamping. Omitted when
 *   the event doesn't touch the score.
 */
interface GameEventBase {
  playerId: string;
}

interface PickupEventData {
  type: PowerUpType;
  variant: PowerUpVariant;
}

export type GameEvent =
  | (GameEventBase & {
      type: GameEventType.FOOD_EATEN;
      position: Position;
      delta: number;
    })
  | (GameEventBase & {
      type: GameEventType.RACING_FOOD_SPAWNED;
      position: Position;
    })
  | (GameEventBase & {
      type: GameEventType.RACING_FOOD_WON;
      position: Position;
      delta: number;
    })
  | (GameEventBase & {
      type: GameEventType.RACING_FOOD_TAKEN;
      position: Position;
      data: { winnerId: string };
    })
  | (GameEventBase & {
      type: GameEventType.RACING_FOOD_CONTESTED;
      position: Position;
    })
  | (GameEventBase & {
      type: GameEventType.RACING_FOOD_EXPIRED;
      position: Position;
    })
  | (GameEventBase & {
      type: GameEventType.DEATH;
      /** Last on-board head position before the crash. */
      position: Position;
      delta: number;
      /** The snake as it was when it crashed, head first — for debris. */
      data: { cause: DeathCause; segments: Position[] };
    })
  | (GameEventBase & {
      type: GameEventType.RESPAWN;
      /** Head of the new snake. */
      position: Position;
    })
  | (GameEventBase & {
      type: GameEventType.ELIMINATED;
      position: Position;
    })
  | (GameEventBase & {
      type: GameEventType.OBSTACLE_HIT;
      position: Position;
    })
  | (GameEventBase & {
      type: GameEventType.BULLDOZER_SMASH;
      position: Position;
    })
  | (GameEventBase & {
      type: GameEventType.BLOCKS_DESTROYED;
      delta: number;
      data: { positions: Position[] };
    })
  | (GameEventBase & {
      type: GameEventType.BLOCK_DENIED;
      position: Position;
      data: { sentBy: string };
    })
  | (GameEventBase & {
      type: GameEventType.BLOCK_SOLIDIFIED;
      position: Position;
      data: { sentBy: string };
    })
  | (GameEventBase & {
      type: GameEventType.BLOCK_CHARGED;
      data: { storedBlocks: number };
    })
  | (GameEventBase & {
      type: GameEventType.PICKUP_COLLECTED;
      position: Position;
      /** `slot` is the inventory index the power-up was stored in. */
      data: PickupEventData & { slot: number };
    })
  | (GameEventBase & {
      type: GameEventType.PICKUP_LOST;
      position: Position;
      data: PickupEventData;
    })
  | (GameEventBase & {
      type: GameEventType.PICKUP_EXPIRED;
      position: Position;
      data: PickupEventData;
    })
  | (GameEventBase & {
      type: GameEventType.PEAK_LENGTH_BONUS;
      delta: number;
    })
  | (GameEventBase & {
      type: GameEventType.TITLE_GAINED;
      /** The title bonus is added at match end, not to `Player.score`. */
      delta: number;
    })
  | (GameEventBase & {
      type: GameEventType.TITLE_LOST;
      delta: number;
    });

/** Narrow `GameEvent` to one member by its `type`. */
export type GameEventOf<T extends GameEventType> = Extract<
  GameEvent,
  { type: T }
>;
